/**
 * Scroll-scrubbed camera path for the wedding plot-plane journey.
 *
 * Storyboard anchors are kept in order and scaled into a world where each
 * photograph is large enough to read on the approach. The camera always
 * frames the active plane with a fit-contain distance so heads stay inside
 * the portrait and landscape frustums, except the hands portal, which is
 * allowed to fly through the thumb gap.
 */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface FacePoint {
  /** Fraction of the plane half-width. +x is image-right. */
  x: number;
  /** Fraction of the plane half-height. +y is image-up. */
  y: number;
}

export interface PlotScene {
  id: 'sangeet' | 'mehndi' | 'hands' | 'ring' | 'havan' | 'pheras';
  asset: string;
  title: string;
  line: string;
  anchor: Vec3;
  /** Points that must stay inside the frame on face-safe keyframes. */
  faces: FacePoint[];
}

export interface JourneyKeyframe {
  at: number;
  sceneIndex: number;
  /** Multiple of the aspect-fit distance. Negative flies past the plane. */
  standoff: number;
  lookX: number;
  lookY: number;
  /** 0 keeps the camera on the plane center; 1 sits it on the look point. */
  cameraBias: number;
  glow: number;
  handsOpacity: number;
  protectFaces: boolean;
}

export interface JourneySample {
  position: Vec3;
  lookAt: Vec3;
  sceneIndex: number;
  /** Fractional scene index, 0 … sceneCount-1, for crossfades. */
  sceneBlend: number;
  glow: number;
  handsOpacity: number;
  scene: PlotScene;
}

/** Storyboard world units are compressed so the next plane reads as a postcard, not a speck. */
export const STORYBOARD_SCALE = 0.2;

export const PLANE_WIDTH = 20;
/** Source photographs are 1376×768. */
export const IMAGE_ASPECT = 1376 / 768;
export const PLANE_HEIGHT = PLANE_WIDTH / IMAGE_ASPECT;
export const CAMERA_FOV = 38;
export const ASPECT_PORTRAIT = 9 / 16;
export const ASPECT_LANDSCAPE = 16 / 9;

export const ASSET_BASE = '/assets/templates/exp-wedding-plot-plane-3d';

const v = (x: number, y: number, z: number): Vec3 => ({ x, y, z });

export const PLOT_SCENES: PlotScene[] = [
  {
    id: 'sangeet',
    asset: '01-couple-sangeet-wide.jpg',
    title: 'Sangeet',
    line: 'Lights, marigolds, and the first dance of the night',
    anchor: v(0, 0, 0),
    faces: [
      { x: -0.12, y: 0.22 },
      { x: 0.14, y: 0.2 },
    ],
  },
  {
    id: 'mehndi',
    asset: '02a-bride-mehndi-wide.jpg',
    title: 'Mehndi',
    line: 'Henna in a courtyard of marigolds',
    anchor: v(0, 0, -800 * STORYBOARD_SCALE),
    faces: [{ x: -0.18, y: 0.2 }],
  },
  {
    id: 'hands',
    asset: '02b-mehndi-hands-closeup.jpg',
    title: 'Mehndi',
    line: 'Through the gap between the thumbs',
    anchor: v(0, 0, -1400 * STORYBOARD_SCALE),
    faces: [],
  },
  {
    id: 'ring',
    asset: '03-ring-ceremony-topright.jpg',
    title: 'Ring Ceremony',
    line: 'A flare of light, and a promise',
    anchor: v(250 * STORYBOARD_SCALE, -180 * STORYBOARD_SCALE, -2200 * STORYBOARD_SCALE),
    faces: [
      { x: -0.42, y: 0.08 },
      { x: -0.22, y: 0.04 },
    ],
  },
  {
    id: 'havan',
    asset: '04-wedding-havan-ritual.jpg',
    title: 'Havan',
    line: 'Into the flame and the embers',
    anchor: v(0, 80 * STORYBOARD_SCALE, -3000 * STORYBOARD_SCALE),
    faces: [
      { x: -0.16, y: 0.22 },
      { x: 0.2, y: 0.18 },
    ],
  },
  {
    id: 'pheras',
    asset: '05-saat-pheras-ritual.jpg',
    title: 'Saat Pheras',
    line: 'The mandap, wide and golden',
    anchor: v(0, 0, -4000 * STORYBOARD_SCALE),
    faces: [
      { x: -0.06, y: 0.26 },
      { x: 0.14, y: 0.24 },
    ],
  },
];

export const JOURNEY_KEYFRAMES: JourneyKeyframe[] = [
  { at: 0, sceneIndex: 0, standoff: 1.12, lookX: 0, lookY: 0.02, cameraBias: 0, glow: 0, handsOpacity: 1, protectFaces: true },
  { at: 0.14, sceneIndex: 0, standoff: 0.96, lookX: 0.04, lookY: 0.05, cameraBias: 0.12, glow: 0, handsOpacity: 1, protectFaces: true },
  { at: 0.26, sceneIndex: 1, standoff: 1.08, lookX: -0.02, lookY: 0.02, cameraBias: 0.04, glow: 0, handsOpacity: 1, protectFaces: true },
  { at: 0.38, sceneIndex: 1, standoff: 0.98, lookX: 0.12, lookY: 0, cameraBias: 0.1, glow: 0, handsOpacity: 1, protectFaces: true },
  { at: 0.48, sceneIndex: 2, standoff: 0.7, lookX: 0, lookY: -0.06, cameraBias: 0.4, glow: 0, handsOpacity: 1, protectFaces: false },
  { at: 0.56, sceneIndex: 2, standoff: 0.2, lookX: 0, lookY: -0.08, cameraBias: 0.9, glow: 0.08, handsOpacity: 1, protectFaces: false },
  { at: 0.62, sceneIndex: 2, standoff: -0.22, lookX: 0, lookY: 0, cameraBias: 0, glow: 0.2, handsOpacity: 0, protectFaces: false },
  { at: 0.72, sceneIndex: 3, standoff: 1.08, lookX: 0, lookY: 0, cameraBias: 0, glow: 0.55, handsOpacity: 0, protectFaces: true },
  { at: 0.8, sceneIndex: 3, standoff: 1.02, lookX: 0.35, lookY: -0.3, cameraBias: 0.18, glow: 1, handsOpacity: 0, protectFaces: true },
  { at: 0.88, sceneIndex: 4, standoff: 1.08, lookX: 0, lookY: -0.04, cameraBias: 0.05, glow: 0.65, handsOpacity: 0, protectFaces: true },
  { at: 0.93, sceneIndex: 4, standoff: 0.98, lookX: 0.02, lookY: -0.16, cameraBias: 0.12, glow: 0.28, handsOpacity: 0, protectFaces: true },
  { at: 1, sceneIndex: 5, standoff: 1.24, lookX: 0, lookY: 0.03, cameraBias: 0, glow: 0, handsOpacity: 0, protectFaces: true },
];

export function assetUrl(file: string): string {
  return `${ASSET_BASE}/${file}`;
}

export function fitDistance(aspect: number, padding = 1.04): number {
  const safeAspect = Math.max(0.35, aspect);
  const tanHalf = Math.tan((CAMERA_FOV * Math.PI) / 360);
  const distH = PLANE_HEIGHT / 2 / tanHalf;
  const distW = PLANE_WIDTH / 2 / (tanHalf * safeAspect);
  return Math.max(distH, distW) * padding;
}

export function clamp01(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function sub(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

function length(a: Vec3): number {
  return Math.hypot(a.x, a.y, a.z);
}

function normalize(a: Vec3): Vec3 {
  const len = length(a) || 1;
  return { x: a.x / len, y: a.y / len, z: a.z / len };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpVec(a: Vec3, b: Vec3, t: number): Vec3 {
  return { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t), z: lerp(a.z, b.z, t) };
}

function smoothstep(t: number): number {
  const u = clamp01(t);
  return u * u * (3 - 2 * u);
}

export function poseForKeyframe(frame: JourneyKeyframe, aspect: number): { position: Vec3; lookAt: Vec3 } {
  const scene = PLOT_SCENES[frame.sceneIndex];
  const fit = fitDistance(aspect);
  const lookOnPlane: Vec3 = {
    x: scene.anchor.x + frame.lookX * (PLANE_WIDTH / 2),
    y: scene.anchor.y + frame.lookY * (PLANE_HEIGHT / 2),
    z: scene.anchor.z,
  };
  const position: Vec3 = {
    x: scene.anchor.x + (lookOnPlane.x - scene.anchor.x) * frame.cameraBias,
    y: scene.anchor.y + (lookOnPlane.y - scene.anchor.y) * frame.cameraBias,
    z: scene.anchor.z + frame.standoff * fit,
  };

  if (frame.standoff < 0) {
    const next = PLOT_SCENES[Math.min(PLOT_SCENES.length - 1, frame.sceneIndex + 1)];
    return { position, lookAt: { ...next.anchor } };
  }

  return { position, lookAt: lookOnPlane };
}

export function sampleJourney(t: number, aspect: number): JourneySample {
  const progress = clamp01(t);
  const frames = JOURNEY_KEYFRAMES;
  let index = 0;
  while (index < frames.length - 2 && frames[index + 1].at < progress) index += 1;

  const from = frames[index];
  const to = frames[Math.min(frames.length - 1, index + 1)];
  const span = Math.max(1e-6, to.at - from.at);
  const local = smoothstep((progress - from.at) / span);
  const fromPose = poseForKeyframe(from, aspect);
  const toPose = poseForKeyframe(to, aspect);
  const sceneBlend = lerp(from.sceneIndex, to.sceneIndex, local);
  const sceneIndex = Math.max(0, Math.min(PLOT_SCENES.length - 1, Math.round(sceneBlend)));

  return {
    position: lerpVec(fromPose.position, toPose.position, local),
    lookAt: lerpVec(fromPose.lookAt, toPose.lookAt, local),
    sceneIndex,
    sceneBlend,
    glow: lerp(from.glow, to.glow, local),
    handsOpacity: lerp(from.handsOpacity, to.handsOpacity, local),
    scene: PLOT_SCENES[sceneIndex],
  };
}

export function progressForScene(sceneIndex: number): number {
  const frames = JOURNEY_KEYFRAMES.filter((item) => item.sceneIndex === sceneIndex);
  const holding = frames.filter((item) => item.standoff > 0 && item.handsOpacity > 0.5);
  const frame = holding.at(-1) ?? frames.at(-1);
  return frame?.at ?? 0;
}

export function fadeOpacities(t: number): number[] {
  const blend = sampleJourney(t, 1).sceneBlend;
  const base = Math.max(0, Math.min(PLOT_SCENES.length - 1, Math.floor(blend + 1e-6)));
  const frac = blend - base;
  return PLOT_SCENES.map((_, i) => {
    if (i === base) return 1;
    if (i === base + 1) return frac;
    return 0;
  });
}

export interface NdcPoint {
  x: number;
  y: number;
  inFront: boolean;
}

/** Perspective project a world point into NDC. +1 is the frustum edge. */
export function projectToNdc(
  point: Vec3,
  camPos: Vec3,
  lookAt: Vec3,
  aspect: number,
  fovDeg = CAMERA_FOV,
): NdcPoint {
  const forward = normalize(sub(lookAt, camPos));
  let right = cross(forward, { x: 0, y: 1, z: 0 });
  if (length(right) < 1e-5) right = cross(forward, { x: 1, y: 0, z: 0 });
  right = normalize(right);
  const up = normalize(cross(right, forward));
  const rel = sub(point, camPos);
  const xCam = dot(rel, right);
  const yCam = dot(rel, up);
  const zForward = dot(rel, forward);
  const tanHalf = Math.tan((fovDeg * Math.PI) / 360);
  if (zForward <= 1e-4) return { x: 0, y: 0, inFront: false };
  return {
    x: xCam / (zForward * tanHalf * Math.max(0.35, aspect)),
    y: yCam / (zForward * tanHalf),
    inFront: true,
  };
}

export function faceWorldPoint(scene: PlotScene, face: FacePoint): Vec3 {
  return {
    x: scene.anchor.x + face.x * (PLANE_WIDTH / 2),
    y: scene.anchor.y + face.y * (PLANE_HEIGHT / 2),
    z: scene.anchor.z,
  };
}
